import * as React from "react";
import { useEffect, useState, useRef } from "react";

import { useNavigate, useLocation } from "react-router-dom";

import useCertificationReportStyles from "./styles";
import { AnalyzeResult, ResolutionStatus } from "../../../../models/benchly.models";
import { CitationStorageService } from "../../../../service/citationStorage.service";
import { DocumentActionService } from "../../../../service/documentActions.service";

const CertificationReport: React.FC = () => {
  const styles = useCertificationReportStyles();
  const [results, setResults] = useState<AnalyzeResult[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const citationRefs = useRef<(HTMLDivElement | null)[]>([]);

  const certified = results
    ? results
        .flatMap((r) => r.citations)
        .filter((c) => c.status === ResolutionStatus.ValidatedAndCertified)
    : [];

  const nonCertified = results
    ? results
        .flatMap((r) => r.citations)
        .filter((c) => c.status !== ResolutionStatus.ValidatedAndCertified)
    : [];

  useEffect(() => {
    let isMounted = true;
    setResults(null);
    setLoading(true);
    setError(null);

    CitationStorageService.getAllCitationResults()
      .then((store) => {
        if (isMounted) setResults(store.results);
      })
      .catch((e) => {
        if (isMounted) setError(e?.message || "Failed to load citation results.");
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });
    return () => {
      isMounted = false;
    };
  }, []);

  // Scroll to citation when returning from ViewCases
  useEffect(() => {
    const state = location.state as { scrollToCitationIndex?: number } | null;

    if (state?.scrollToCitationIndex !== undefined && !loading && citationRefs.current.length > 0) {
      const targetIndex = state.scrollToCitationIndex;
      const targetElement = citationRefs.current[targetIndex];

      if (targetElement) {
        // Small delay to ensure rendering is complete
        setTimeout(() => {
          targetElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });

          // Add a brief highlight effect
          targetElement.style.backgroundColor = "#e3f2fd";
          setTimeout(() => {
            targetElement.style.backgroundColor = "";
          }, 1500);
        }, 100);
      }

      // Clear the state after scrolling
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [loading, location.state, location.pathname, navigate]);
  return (
    <article className={styles.container}>
      <div className={styles.reportHeader}>
        <h1 className={styles.reportTitle}>Certification Report</h1>
      </div>
      <div className={styles.reportContent}>
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Summary Report</h2>
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Validated and Certified</h3>
            <div className={styles.citationsList}>
              {loading ? (
                <span>Loading...</span>
              ) : error ? (
                <span className={styles.noCitations}>{error}</span>
              ) : certified.length === 0 ? (
                <span className={styles.noCitations}>No certified citations.</span>
              ) : (
                certified.map((c, i) => {
                  const parentResult = results?.find((r) => r.citations.includes(c));
                  const documentReferenceId = parentResult?.documentReferenceId;
                  return c.foundDocs && c.foundDocs.length > 0 ? (
                    <div
                      ref={(el) => {
                        citationRefs.current[i] = el;
                      }}
                      className={styles.citationItem}
                      key={i}
                      onClick={() => {
                        navigate("/view-cases", {
                          state: {
                            documentReferenceId,
                            returnToCitationIndex: i,
                          },
                        });
                      }}
                    >
                      {c.foundDocs[0].title} {c.foundDocs[0].identifier} (
                      {new Date(c.foundDocs[0].docDate).getFullYear()})
                    </div>
                  ) : null;
                })
              )}
            </div>
          </div>
          <div className={styles.subsection}>
            <h3 className={styles.subsectionTitle}>Non-Certified</h3>
            <div className={styles.citationsList}>
              {loading ? (
                <span>Loading...</span>
              ) : error ? (
                <span className={styles.noCitations}>{error}</span>
              ) : nonCertified.length === 0 ? (
                <span className={styles.noCitations}>No non-certified citations.</span>
              ) : (
                nonCertified.map((c, i) => {
                  const parentResult = results?.find((r) => r.citations.includes(c));
                  const documentReferenceId = parentResult?.documentReferenceId;
                  return c.foundDocs && c.foundDocs.length > 0 ? (
                    <div
                      className={styles.citationItem}
                      key={i}
                      onClick={() => {
                        if (documentReferenceId)
                          DocumentActionService.selectCitation(documentReferenceId);
                      }}
                    >
                      {c.foundDocs[0].title} {c.foundDocs[0].identifier} (
                      {new Date(c.foundDocs[0].docDate).getFullYear()})
                    </div>
                  ) : null;
                })
              )}
            </div>
          </div>
        </div>

        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Detailed Report</h2>
          {loading ? (
            <span>Loading...</span>
          ) : error ? (
            <span className={styles.noCitations}>{error}</span>
          ) : results && results.length > 0 ? (
            // Show one row per AnalyzeResult (bookmark), using resolvedLawCase if present,
            // otherwise first certified lawcase, otherwise first lawcase.
            results.map((r, idx) => {
              const lawCase =
                r.resolvedLawCase && r.resolvedLawCase !== "none"
                  ? r.resolvedLawCase
                  : (r.citations?.find(
                      (c) => c.status === ResolutionStatus.ValidatedAndCertified
                    ) ?? r.citations?.[0]);

              if (!lawCase) return null;

              const doc =
                lawCase.foundDocs && lawCase.foundDocs.length > 0 ? lawCase.foundDocs[0] : null;
              const isNonCertified = lawCase.status !== ResolutionStatus.ValidatedAndCertified;

              return (
                <div
                  className={styles.detailedCitation}
                  key={r.documentReferenceId ?? idx}
                  id={isNonCertified ? `noncert-${idx}` : undefined}
                  onClick={() => {
                    if (r.documentReferenceId) {
                      DocumentActionService.selectCitation(r.documentReferenceId);
                    }
                  }}
                >
                  <h4 className={styles.citationTitle}>
                    {doc ? doc.title : lawCase.titleOnDocument}
                  </h4>

                  <div className={styles.citationDetails}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Citation:</span>
                      <span className={styles.detailValue}>
                        {doc ? doc.identifier : lawCase.identifierOnDocument}
                      </span>
                    </div>

                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Date of Opinion:</span>
                      <span className={styles.detailValue}>
                        {doc?.docDate ? new Date(doc.docDate).getFullYear() : "-"}
                      </span>
                    </div>

                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Court:</span>
                      <span className={styles.detailValue}>
                        {doc ? doc.courtAbbreviation || doc.court || "-" : "-"}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <span className={styles.noCitations}>No citation results found.</span>
          )}
        </div>
      </div>
    </article>
  );
};

export default CertificationReport;
